import { Box, Button, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/system';
import { isPassed } from 'utils/lockDuration';
import { WITHDRAW_ADDRESS, WITHDRAW_LOCK_TIME } from 'data/constant';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { getUserDeposit, UserDepositParams, UserWithdrawDeposit } from 'apis';
import { toast } from 'react-toastify';
// import { useEthersSigner } from 'hooks/useEtherSigner';
import { ConfirmationModal } from 'pages/Landing/components/ConfirmationModal';
import AtlasLogo from 'assets/images/atlas.png';
import { useAptosWalletContext } from 'context/aptosProvider';

const TextBox = styled('div')<{ color: string }>`
	padding: 6px 10px;
	background-color: ${({ color }) => `${color}22`};
	border: ${({ color }) => `1px solid ${color}`};
	border-radius: 4px;
	height: 35px;
	font-size: 14px;
`;

const StyledButton = styled(Button)<{ disabled?: boolean }>`
	display: flex;
	padding: 12px 32px;
	box-shadow: 0 4px 4px transparent;
	border-radius: 8px;
	overflow: hidden;
	background-color: ${({ disabled }) => (disabled ? '#dadada' : '#2dbcb8')};

	:hover {
		background-color: #cce3c0;
	}
`;

const Withdraw = styled('div')`
	display: flex;
	flex-direction: column;
	width: 100%;
	//max-width: 500px;
	height: 100%;
	padding: 0 10px 40px 10px;
`;

const BorderBox = styled(Withdraw)`
	position: relative;
	height: 100%;
	background-color: #fff;
	clip-path: polygon(
		15px 0,
		100% 0,
		100% calc(100% - 15px),
		calc(100% - 15px) 100%,
		0 100%,
		0 15px
	);

	&:before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		border: 1px solid #8a8c92;
		clip-path: inherit;
		pointer-events: none;
	}
	padding: 20px;
`;

const TopLine = styled('div')`
	position: absolute;
	top: -2px;
	left: 16.5px;
	width: 1px;
	height: 25px;
	background-color: rgb(138, 140, 146);
	transform: rotate(45deg);
	transform-origin: left top;
	z-index: 13;
`;

const BottomLine = styled('div')`
	position: absolute;
	width: 1px;
	height: 25px;
	background-color: rgb(138, 140, 146);
	transform: rotate(45deg);
	transform-origin: left top;
	z-index: 13;
	bottom: -9px;
	right: -0.5px;
`;

const Column = styled('div')`
	display: flex;
	flex-direction: column;
`;

const PrimaryButton = styled(Button)(() => ({
	backgroundColor: '#345CF8',
	//width: '100%',
	whiteSpace: 'nowrap',
	color: '#FFFFFF',
	padding: '6px 15px',
	fontFamily: 'AtAmiga',
	fontWeight: 600,
	fontSize: '14px',
	letterSpacing: '0.15em',
	textTransform: 'uppercase',
	boxShadow: 'none',
	clipPath:
		'polygon(15px 0, 100% 0, 100% calc(100% - 15px), calc(100% - 15px) 100%, 0 100%, 0 15px)',
	'&:hover': {
		backgroundColor: '#2a4be0',
		boxShadow: 'none',
	},

	'&:disabled': {
		backgroundColor: '#748cf6',
		boxShadow: 'none',
	},
}));

const Row = styled('div')`
	display: flex;
	flex-direction: row;
`;

export const Header = ({
	claims,
	contractBal,
	onSuccess,
}: {
	claims: UserWithdrawDeposit[];
	contractBal: string;
	onSuccess?(): void;
}) => {
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [balance, setBalance] = useState('0');
	const [loading, setLoading] = useState(false);
	const [withdrawAddress, setWithdrawAddress] = useState(WITHDRAW_ADDRESS);
	const [userDeposits, setUserDeposits] = useState<{
		lastDeposit: Required<UserWithdrawDeposit>[];
	}>({ lastDeposit: [] });
	const [amount, setAmount] = useState(contractBal);
	const selectedVault = useSelector((state: RootState) => state.vault);
	const appVault = useSelector((state: RootState) => state.vault);
	//const { address: account, isConnected } = useAccount();
	const selectedExchange = selectedVault.exchanger.find(
		(d) => d.value === selectedVault.exchange,
	);
	const selectedContract = selectedVault.exchanger.find(
		(d) => d.value === appVault.exchange,
	);
	const selectedAsset = selectedVault.token.find(
		(d) => d.name === selectedVault.selectedToken,
	);
	const { account, isConnected } = useAptosWalletContext();
	const lockPassed =
		userDeposits?.lastDeposit.length > 0
			? isPassed(
					userDeposits?.lastDeposit?.[
						userDeposits?.lastDeposit.length - 1
					]?.createdAt,
					WITHDRAW_LOCK_TIME,
			  )
			: { isLocked: false, newTime: moment().utc() };

	const payload = useMemo(() => {
		return {
			contract: selectedExchange?.depositContractAddress || '',
			address: account || '',
		};
	}, [account, selectedExchange?.depositContractAddress]);
	const canWithdraw = claims.some((item) => item.claimStatus === 'processed');
	//const signer: any = useEthersSigner();

	const claimType = useMemo<{
		type: 'claim' | 'claim under processing' | 'processed';
	}>(() => {
		if (
			(!claims || claims.length === 0) &&
			userDeposits.lastDeposit.length > 0
		) {
			return { type: 'claim' };
		}

		const hasProcessing = claims.some(
			({ eventType, claimStatus }) =>
				eventType === 'claim' && claimStatus === 'processing',
		);
		if (hasProcessing) {
			return { type: 'claim under processing' };
		}

		const hasProcessed = claims.some(
			({ eventType, claimStatus }) =>
				eventType === 'claim' && claimStatus === 'processed',
		);
		if (hasProcessed) {
			return { type: 'processed' };
		}

		return { type: 'claim' };
	}, [claims, userDeposits.lastDeposit.length]);

	/*	const getWithdrawAddress = useCallback(async () => {
		try {
			const depContract = getNativeDepositContract(
				selectedContract?.depositContractAddress || '',
				signer?.provider.getSigner(account).connectUnchecked(),
			);

			const withdrawContractAddress =
				await depContract.getWithdrawManager();

			setWithdrawAddress(withdrawContractAddress);

			return;
		} catch (e: any) {
			//
		}
	}, [account, selectedContract?.depositContractAddress, signer?.provider]);*/

	const fetchBalance = useCallback(
		async () => {
			/*try {
			if (account) {
				const assetContract = getErc20Contract(
					selectedAsset?.address || '',
					signer?.provider.getSigner(account).connectUnchecked(),
				);
				const assetBalance = await assetContract.balanceOf(account);
				setBalance(
					formatUnits(assetBalance, selectedAsset?.decimalValue),
				);
			} else {
				setBalance('0');
			}
		} catch (e) {
			//
		}*/
		},
		[
			/*account, signer, selectedAsset?.address, selectedAsset?.decimalValue*/
		],
	);

	const withdraw = async () => {
		/*try {
			setLoading(true);

			const res = await getUserDeposit(payload);
			setUserDeposits(res);
			if (res.lastDeposit.length > 0) {
				const depContract = getDepositContract(
					selectedContract?.depositContractAddress || '',
					signer?.provider.getSigner(account).connectUnchecked(),
				);

				const withdrawContractAddress =
					await depContract.getWithdrawManager();

				const contract = getWithdrawalContract(
					withdrawContractAddress, //selectedContract?.withdrawContractAddress || '',
					signer?.provider.getSigner(account).connectUnchecked(),
				);

				const tx2 = await contract.withdraw(
					selectedContract?.depositContractAddress,
					account,
				);
				await tx2.wait();

				await userDeposit({
					contract: selectedContract?.depositContractAddress || '',
					address: account || '',
					amount,
					token: 'USDC',
					eventType: 'withdraw',
				});
				toast('Successfully withdrawn from vault contract', {
					type: 'success',
				});
				setAmount('0');
				onSuccess?.();
				return;
			}
		} catch (e: any) {
			onSuccess?.();
			toast(
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: '0.75rem',
					}}
				>
					<div>
						{getErrorMessage(e?.error?.code || e?.code, e) ||
							'Something went wrong'}
					</div>
					{getErrorMessage(e?.error?.code || e?.code, e).includes(
						'Something went wrong',
					) && (
						<StyledButton
							onClick={() => {
								window.open(
									'https://t.me/+96H-v4meO1szMmJk',
									'_blank',
								);
							}}
							style={{ height: 'min-content', padding: 5 }}
						>
							<Typography
								variant="caption"
								fontWeight="bold"
								py="6px"
								color="#fff"
								style={{ lineHeight: '120%' }}
							>
								Support
							</Typography>
						</StyledButton>
					)}
				</div>,
				{
					type: 'error',
				},
			);
		} finally {
			fetchBalance();
			setLoading(false);
		}*/
	};

	const claim = async () => {
		/*try {
			setLoading(true);

			const res = await getUserDeposit(payload);
			setUserDeposits(res);

			if (res.lastDeposit.length > 0) {
				if (!lockPassed.isLocked) {
					const requiredAmount = parseUnits(
						contractBal /!*calculateNetValue(
							userDeposits.lastDeposit,
							claims,
						).toString()*!/,
						selectedAsset?.decimalValue || 18,
					);

					const depContract = getDepositContract(
						selectedContract?.depositContractAddress || '',
						signer?.provider.getSigner(account).connectUnchecked(),
					);

					const depositTx = await depContract.approve(
						withdrawAddress, //selectedContract?.withdrawContractAddress,
						requiredAmount,
					);

					await depositTx.wait();

					const contract = getWithdrawalContract(
						withdrawAddress, //selectedContract?.withdrawContractAddress || '',
						signer?.provider.getSigner(account).connectUnchecked(),
					);

					const tx = await contract.claim(
						selectedContract?.depositContractAddress,
						requiredAmount,
					);
					await tx.wait();

					await userDeposit({
						contract:
							selectedContract?.depositContractAddress || '',
						address: account || '',
						amount,
						token: 'USDC',
						eventType: 'claim',
					});
					toast('Successfully claim from vault contract', {
						type: 'success',
					});

					onSuccess?.();
					return;
				}
				toast(
					`Claim after ${lockPassed.newTime.format(
						'DD-MMM, hh:mm A',
					)}`,
				);
			}
		} catch (e: any) {
			onSuccess?.();
			toast(
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: '0.75rem',
					}}
				>
					<div>
						{getErrorMessage(e?.error?.code || e?.code, e) ||
							'Something went wrong'}
					</div>
					{getErrorMessage(e?.error?.code || e?.code, e).includes(
						'Something went wrong',
					) && (
						<StyledButton
							onClick={() => {
								window.open(
									'https://t.me/+96H-v4meO1szMmJk',
									'_blank',
								);
							}}
							style={{ height: 'min-content', padding: 5 }}
						>
							<Typography
								variant="caption"
								fontWeight="bold"
								py="6px"
								color="#fff"
								style={{ lineHeight: '120%' }}
							>
								Support
							</Typography>
						</StyledButton>
					)}
				</div>,
				{
					type: 'error',
				},
			);
		} finally {
			fetchBalance();
			setLoading(false);
		}*/
	};

	const onNativeClaim = async () => {
		setLoading(true);
		/*try {
			const res = await getUserDeposit(payload);
			setUserDeposits(res);

			const { lastDeposit } = res;
			if (!lastDeposit.length) {
				toast('No previous deposit found to claim.', { type: 'info' });
				return;
			}

			if (lockPassed.isLocked) {
				toast(
					`Claim available after ${lockPassed.newTime.format(
						'DD-MMM, hh:mm A',
					)}`,
				);
				return;
			}

			const depositAddr = selectedContract?.depositContractAddress || '';
			const provider = signer?.provider;
			const signerAccount = provider
				.getSigner(account)
				.connectUnchecked();

			const requiredAmount = ethers.utils.parseEther(contractBal);

			/!*const assetContract = getErc20Contract(
				selectedAsset?.address || '',
				signerAccount,
			);

			const txz = assetContract.claim(depositAddr, requiredAmount);
			await txz.wait();*!/

			const depContract = getNativeDepositContract(
				depositAddr,
				signerAccount,
			);

			const depositTx = await depContract.approve(
				withdrawAddress, //selectedContract?.withdrawContractAddress,
				requiredAmount,
			);

			await depositTx.wait();

			const contract = getNativeWithdrawalContract(
				withdrawAddress,
				signerAccount,
			);

			const tx = await contract.claim(depositAddr, requiredAmount);
			await tx.wait();

			await userDeposit({
				contract: selectedContract?.depositContractAddress || '',
				address: account || '',
				amount,
				token: selectedAsset?.name,
				eventType: 'claim',
			});
			toast('Successfully claim from vault contract', {
				type: 'success',
			});

			onSuccess?.();
		} catch (e: any) {
			onSuccess?.();
			toast(
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: '0.75rem',
					}}
				>
					<div>
						{getErrorMessage(e?.error?.code || e?.code, e) ||
							'Something went wrong'}
					</div>
					{getErrorMessage(e?.error?.code || e?.code, e).includes(
						'Something went wrong',
					) && (
						<StyledButton
							onClick={() => {
								window.open(
									'https://t.me/+96H-v4meO1szMmJk',
									'_blank',
								);
							}}
							style={{ height: 'min-content', padding: 5 }}
						>
							<Typography
								variant="caption"
								fontWeight="bold"
								py="6px"
								color="#fff"
								style={{ lineHeight: '120%' }}
							>
								Support
							</Typography>
						</StyledButton>
					)}
				</div>,
				{
					type: 'error',
				},
			);
		} finally {
			fetchBalance();
			setLoading(false);
		}*/
	};

	const onNativeWithdraw = async () => {
		/*setLoading(true);
		try {
			const res = await getUserDeposit(payload);
			setUserDeposits(res);

			const { lastDeposit } = res;
			if (!lastDeposit.length) {
				toast('No previous deposit found to withdraw.', {
					type: 'info',
				});
				return;
			}

			/!*const depContract = getNativeDepositContract(
				selectedContract?.depositContractAddress || '',
				signer?.provider.getSigner(account).connectUnchecked(),
			);*!/

			/!*TODO: Remove this later*!/
			const withdrawContractAddress = withdrawAddress;
			//await depContract.getWithdrawManager();

			const contract = getNativeWithdrawalContract(
				withdrawContractAddress,
				signer?.provider.getSigner(account).connectUnchecked(),
			);

			const tx2 = await contract.withdrawNative(
				selectedContract?.depositContractAddress,
				account,
			);

			await tx2.wait();

			await userDeposit({
				contract: selectedContract?.depositContractAddress || '',
				address: account || '',
				amount,
				token: selectedAsset?.name,
				eventType: 'withdraw',
			});
			toast('Successfully withdrawn from vault contract', {
				type: 'success',
			});
			setAmount('0');
			onSuccess?.();
			return;
		} catch (e: any) {
			onSuccess?.();
			toast(
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: '0.75rem',
					}}
				>
					<div>
						{getErrorMessage(e?.error?.code || e?.code, e) ||
							'Something went wrong'}
					</div>
					{getErrorMessage(e?.error?.code || e?.code, e).includes(
						'Something went wrong',
					) && (
						<StyledButton
							onClick={() => {
								window.open(
									'https://t.me/+96H-v4meO1szMmJk',
									'_blank',
								);
							}}
							style={{ height: 'min-content', padding: 5 }}
						>
							<Typography
								variant="caption"
								fontWeight="bold"
								py="6px"
								color="#fff"
								style={{ lineHeight: '120%' }}
							>
								Support
							</Typography>
						</StyledButton>
					)}
				</div>,
				{
					type: 'error',
				},
			);
		} finally {
			fetchBalance();
			setLoading(false);
		}*/
	};

	const getUserDeposits = useCallback(async (value: UserDepositParams) => {
		try {
			const res = await getUserDeposit(value);
			setUserDeposits(res);
		} catch (e) {
			//
		}
	}, []);

	const BalanceComponent = () => {
		return (
			<>
				<Column>
					<Typography variant={'subtitle1'} fontWeight={600}>
						Amount in Vault
					</Typography>
					<Box height={10} />
					<Row style={{ alignItems: 'center' }}>
						<img
							src={selectedVault.token[0].icon}
							height={24}
							width={24}
							alt="USDC"
						/>
						<Box width={10} />
						<Typography variant={'subtitle1'}>
							{contractBal}
						</Typography>
					</Row>
				</Column>
				{lockPassed.isLocked && (
					<PrimaryButton
						style={{ height: 42 }}
						onClick={() => {
							toast(
								`Claim available after ${lockPassed?.newTime.format(
									'DD-MMM, hh:mm A',
								)}`,
							);
						}}
					>
						<Typography
							variant={'subtitle2'}
							fontWeight="bold"
							py="1px"
							color={'#fff'}
						>
							{loading ? 'Please Wait...' : 'CLAIM'}
						</Typography>
						<LockIcon style={{ marginLeft: '10px' }} />
					</PrimaryButton>
				)}

				{claimType.type === 'claim' && !lockPassed.isLocked && (
					<PrimaryButton
						onClick={() => {
							setConfirmationOpen(true);
						}}
						disabled={
							true ||
							loading ||
							!isConnected ||
							lockPassed.isLocked
						}
						sx={{ height: 42 }}
					>
						<Typography
							variant={'subtitle2'}
							fontWeight="bold"
							py="1px"
							color={'#fff'}
						>
							{loading ? 'Please Wait...' : 'CLAIM'}
						</Typography>
					</PrimaryButton>
				)}
				{canWithdraw && (
					<PrimaryButton
						onClick={() => {
							if (selectedAsset?.isNative) {
								onNativeWithdraw();
							} else {
								withdraw();
							}
						}}
						disabled={
							true ||
							loading ||
							!isConnected ||
							lockPassed.isLocked
						}
						sx={{ height: 42 }}
					>
						<Typography
							variant={'subtitle2'}
							fontWeight="bold"
							py="1px"
							color={'#fff'}
						>
							{loading ? 'Please Wait...' : 'Withdraw'}
						</Typography>
					</PrimaryButton>
				)}
				{claimType.type === 'claim under processing' && (
					<TextBox color={'#FABD10'}>
						<Typography
							variant={'caption'}
							fontWeight="bold"
							color={'#FABD10'}
							fontFamily={'Roboto Mono'}
						>
							{claimType.type.charAt(0).toUpperCase() +
								claimType.type.slice(1).toLowerCase()}
						</Typography>
					</TextBox>
				)}
			</>
		);
	};

	const EmptyComponent = () => {
		return (
			<Row style={{ width: '100%', justifyContent: 'space-between' }}>
				<Column>
					<Typography variant={'caption'} fontWeight={600}>
						Liquidity Maximizer for Perpetual Exchanges.
					</Typography>
				</Column>
				<img src={AtlasLogo} width={32} height={65} alt={'atlas'} />
			</Row>
		);
	};

	useEffect(() => {
		getUserDeposits(payload);
	}, [getUserDeposits, payload]);

	return (
		<BorderBox
			style={{
				paddingLeft: 20,
				flexDirection: 'row',
				justifyContent: 'space-between',
			}}
		>
			{account && parseFloat(contractBal) > 0 ? (
				<BalanceComponent />
			) : (
				<EmptyComponent />
			)}
			<BottomLine />
			<TopLine />
			<ConfirmationModal
				open={confirmationOpen}
				onClose={() => setConfirmationOpen(false)}
				onConfirm={async () => {
					setConfirmationOpen(false);
					if (selectedAsset?.isNative) {
						await onNativeClaim();
					} else {
						await claim();
					}
				}}
			/>
		</BorderBox>
	);
};
